import React, { createContext, useContext, useMemo, useState, useEffect, useCallback, useRef } from 'react';
import debounce from 'lodash/debounce';
import { useDispatch, useSelector } from 'react-redux';
import isEqual from 'lodash/isEqual';
import {
    createColumnHelper,
    useReactTable,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
} from '@tanstack/react-table';
import { viewFullData } from '../../store/extractions/action';
import constants from '../../commonUtils/constants';
import { underscoresToSpaces } from '../../commonUtils/string.helper';
import { getSingleOutputMapping } from '../../store/extOutputMappings/actions';

const ViewFullDataContext = createContext();

export const useViewFullData = () => useContext(ViewFullDataContext);

export const ViewFullDataProvider = ({ children, extVersionKey, dataGroup, dataCategory }) => {
    const dispatch = useDispatch();
    const { FullDataList, fullDataFilters, errors, extLoading } = useSelector((state) => state.Extractions);
    const { extVersion } = useSelector((state) => state.ExtVersions);
    const { extOutputMappingId, loading: outputMappingLoading } = useSelector((state) => state.ExtOutputMappings);

    // --- CLEANUP: Reset full data and output mapping on unmount ---
    useEffect(() => {
        return () => {
            // Reset FullDataList in extractions reducer
            dispatch({ type: 'RESET_FULL_DATA_LIST' });
            // Reset extOutputMappingId in output mappings reducer
            dispatch({ type: 'RESET_SINGLE_OUTPUT_MAPPING' });
        };
    }, [dispatch]);

    const expectedDataGroupTitle    = useMemo(() => underscoresToSpaces(dataGroup    || ''), [dataGroup]);
    const expectedDataCategoryTitle = useMemo(() => underscoresToSpaces(dataCategory || ''), [dataCategory]);

    // -------------------- context match checks --------------------

    const isRouteContextInMeta = useMemo(() => {
        const meta = FullDataList?.meta;
        if (!meta) return false;
        return (
            meta?.extVersion?.key   === extVersionKey &&
            meta?.dataGroup?.title  === expectedDataGroupTitle &&
            meta?.dataCategory?.title === expectedDataCategoryTitle
        );
    }, [FullDataList?.meta, extVersionKey, expectedDataGroupTitle, expectedDataCategoryTitle]);

    const canRequestFullData = useMemo(() => {
        const meta = FullDataList?.meta;
        return Boolean(
            isRouteContextInMeta &&
            meta?.extVersion?.id &&
            meta?.dataGroup?.id &&
            meta?.dataCategory?.id
        );
    }, [FullDataList?.meta, isRouteContextInMeta]);

    const mappingRequestKey = useMemo(() => {
        if (!extVersionKey || !expectedDataGroupTitle || !expectedDataCategoryTitle) return null;
        return `${extVersionKey}::${expectedDataGroupTitle}::${expectedDataCategoryTitle}`;
    }, [extVersionKey, expectedDataGroupTitle, expectedDataCategoryTitle]);

    // -------------------- refs --------------------

    const prevFiltersRef      = useRef();
    const isInitialMount      = useRef(true);
    const requestedMappingKeyRef = useRef(null);

    // -------------------- helpers --------------------

    // Always include the latest extOutputMappingId from Redux
    const requestViewFullData = useCallback((params) => {
        dispatch(viewFullData({ extOutputMappingId, ...params }));
    }, [dispatch, extOutputMappingId]);

    // -------------------- effects --------------------

    // Always fetch output mapping if extOutputMappingId is missing and we have enough context
    useEffect(() => {
        if (!extVersionKey || !dataGroup || !dataCategory) return;
        if (extOutputMappingId) return;
        if (!mappingRequestKey) return;
        if (outputMappingLoading && requestedMappingKeyRef.current === mappingRequestKey) return;
        if (requestedMappingKeyRef.current === mappingRequestKey) return;

        requestedMappingKeyRef.current = mappingRequestKey;
        dispatch(getSingleOutputMapping({
            versionKey: extVersionKey,
            dataGroupTitle: expectedDataGroupTitle,
            dataCategoryTitle: expectedDataCategoryTitle,
        }));
    }, [
        extVersionKey, dataGroup, dataCategory, extOutputMappingId,
        mappingRequestKey, outputMappingLoading,
        expectedDataGroupTitle, expectedDataCategoryTitle, dispatch
    ]);

    // Debounced re-fetch when filters change
    const debouncedRequestViewFullData = useMemo(() => debounce((params) => {
        requestViewFullData(params);
    }, constants.filters.debounceInterval || 500), [requestViewFullData]);

    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            prevFiltersRef.current = fullDataFilters;
            return;
        }

        const filtersChanged = !isEqual(prevFiltersRef.current, fullDataFilters);

        if (canRequestFullData && filtersChanged) {
            debouncedRequestViewFullData({
                currentPage:  1,
                perPage:      FullDataList?.pagination?.perPage || constants.tableUI.defaultPerpage,
                ...fullDataFilters
            });
        }

        prevFiltersRef.current = fullDataFilters;
        // Cleanup debounce on unmount
        return () => debouncedRequestViewFullData.cancel();
    }, [
        fullDataFilters, debouncedRequestViewFullData,
        FullDataList?.meta, FullDataList?.pagination?.perPage,
        extVersion?.id, canRequestFullData
    ]);

    // -------------------- derived data --------------------

    // Pagination comes directly from FullDataList.pagination — single source of truth
    // Shape guaranteed by reducer: { currentPage, perPage, total, totalPages }
    const pagination = useMemo(() => ({
        currentPage: FullDataList?.pagination?.currentPage || 1,
        perPage:     FullDataList?.pagination?.perPage     || constants.tableUI.defaultPerpage,
        total:       FullDataList?.pagination?.total       || 0,
        totalPages:  FullDataList?.pagination?.totalPages  || 0,
    }), [FullDataList?.pagination]);

    const fullData = useMemo(() => (FullDataList?.data || []).map(
        (row, index) => ({
            id: ((pagination.currentPage - 1) * pagination.perPage) + index + 1,
            ...row
        })
    ), [FullDataList?.data, pagination.currentPage, pagination.perPage]);

    const [columnVisibility, setColumnVisibility] = useState({});

    // -------------------- columns --------------------

    const columnHelper = createColumnHelper();

    const excludedColumnKeys = useMemo(() => new Set([
        'datagroup', 'data_group', 'data group',
        'datacategory', 'data_category', 'data category'
    ]), []);

    const columns = useMemo(() => {
        if (!fullData || fullData.length === 0) return [];

        const firstRow = fullData[0];
        const columnNames = Object.keys(firstRow).filter(key => {
            if (key === 'id') return false;
            const normalizedKey = key.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
            return (
                !excludedColumnKeys.has(normalizedKey.replace(/\s+/g, '')) &&
                !excludedColumnKeys.has(normalizedKey)
            );
        });

        return [
            columnHelper.accessor('id', {
                header: '#',
                cell: info => info.getValue(),
                size: 60,
                enableSorting: false,
            }),
            ...columnNames.map(colName =>
                columnHelper.accessor(colName, {
                    header: colName,
                    cell: info => {
                        const value = info.getValue();
                        return value === null || value === undefined ? '-' : String(value);
                    },
                })
            )
        ];
    }, [columnHelper, excludedColumnKeys, fullData]);

    // -------------------- table --------------------

    // pageCount is driven reactively from pagination.totalPages so TanstackPagination
    // always has the correct total page count for rendering page links and enabling
    // the next/prev buttons.
    const table = useReactTable({
        data: fullData || [],
        columns,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        state: {
            columnVisibility,
            pagination: {
                pageIndex: pagination.currentPage - 1,
                pageSize:  pagination.perPage,
            },
        },
        onColumnVisibilityChange: setColumnVisibility,
        manualPagination: true,
        pageCount: pagination.totalPages,  // kept reactive — not just initialState
    });

    // -------------------- handlers --------------------

    const handlePageChange = useCallback((newPage, newPerPage) => {
        if (!canRequestFullData) return;
        // Always pass context IDs for correct saga context matching
        const meta = FullDataList?.meta || {};
        requestViewFullData({
            currentPage:  newPage,
            perPage:      newPerPage,
            ...fullDataFilters,
            extVersion: meta.extVersion?.id,
            dataGroup: meta.dataGroup?.id,
            dataCategory: meta.dataCategory?.id
        });
    }, [requestViewFullData, FullDataList?.meta, fullDataFilters, canRequestFullData]);

    // -------------------- context value --------------------

    return (
        <ViewFullDataContext.Provider value={{
            fullData,
            table,
            columns,
            loading: extLoading,
            isCurrentRouteDataReady: isRouteContextInMeta,
            errors,
            pagination,
            handlePageChange,
            columnVisibility,
            setColumnVisibility,
        }}>
            {children}
        </ViewFullDataContext.Provider>
    );
};